import { Link, useLocation } from 'react-router-dom'
import {
  X, Home, BarChart3, Brain, Map, Calendar,
  Settings, Users, Target, Table, ArrowUpDown, Layers, DollarSign, RefreshCcw,
  FileBarChart2, Mail, Link as LinkIcon,
} from 'lucide-react'
import { useUser } from '../context/UserContext'
import { useState, useEffect } from 'react'
import { theme, T, ensureFonts } from './report-shell/tokens'

// Operator brand colours (match report-shell tokens.op.*).
const OP_COLOR = {
  ORANGE:  '#ff7900',
  PLUS:    '#00a651',
  TMOBILE: '#e20074',
  P4:      '#7b2cbf',
}

const navigation = [
  { name: 'Dashboard główny', href: '/dashboard', icon: Home },
  { name: 'Historia wyników', href: '/history',   icon: Calendar },
  { name: 'Mapa sprzedaży',   href: '/sales-map', icon: Map },
  { name: 'Analiza AI',       href: '/ai-analysis', icon: Brain, requiredFeature: 'AI_ANALYSIS' },
  { type: 'sectionLabel', label: 'Operatorzy' },
  {
    name: 'Orange',
    icon: BarChart3,
    requiredOperator: 'ORANGE',
    isSection: true,
    operatorKey: 'ORANGE',
    items: [
      { name: 'Analiza sprzedaży', href: '/orange-salon-analysis' },
      { name: 'Analiza dystryktów', href: '/orange-districts' },
      { name: 'Sprzedaż vs FTE', href: '/orange-fte-analysis' },
      { name: 'Opaska SOS – Sprzedaż', href: '/orange-opaska-sales', excludeRoles: ['OPERATOR_EXTERNAL'] },
      { name: 'Opaska SOS – VIP / Standard / Zero', href: '/orange-vip-standard-zero', excludeRoles: ['OPERATOR_EXTERNAL'] },
      { name: 'Lokalizator – Sprzedaż', href: '/orange-locator-sales', excludeRoles: ['OPERATOR_EXTERNAL'] },
      { name: 'Lokalizator – VIP / Standard / Zero', href: '/orange-locator-vip-standard-zero', excludeRoles: ['OPERATOR_EXTERNAL'] },
      { name: 'Lokalizator – Sprzedaż per urządzenie', href: '/orange-locator-device-sales', excludeRoles: ['OPERATOR_EXTERNAL'] },
      { name: 'Sprzedaż per Agent', href: '/orange-agent-sales', excludeRoles: ['OPERATOR_EXTERNAL'] },
      { name: 'R1 · Sprzedaż dnia', href: '/orange-daily-sales', excludeRoles: ['OPERATOR_EXTERNAL'] },
      { name: 'R2 · Salony półkowe (PB)', href: '/orange-shelf-sales', excludeRoles: ['OPERATOR_EXTERNAL'] },
      { name: 'R3 · Utracone szanse', href: '/orange-lost-opportunities', excludeRoles: ['OPERATOR_EXTERNAL'] },
      { name: 'Region / Dystrykt (podsumowanie)', href: '/orange-region-summary', excludeRoles: ['OPERATOR_EXTERNAL'] },
    ],
  },
  {
    name: 'Plus',
    icon: BarChart3,
    requiredOperator: 'PLUS',
    isSection: true,
    operatorKey: 'PLUS',
    items: [
      { name: 'Analiza sprzedaży', href: '/plus-salon-analysis' },
      { name: 'Raport sprzedaży [Grafana]', href: '/plus-sales', excludeRoles: ['OPERATOR_EXTERNAL'] },
      { name: 'VIP / Standard / Zero', href: '/plus-vip-standard-zero', excludeRoles: ['OPERATOR_EXTERNAL'] },
      { name: 'Sprzedaż per Agent', href: '/plus-agent-sales', excludeRoles: ['OPERATOR_EXTERNAL'] },
      { name: 'Region / RKS (podsumowanie)', href: '/plus-region-rollup', excludeRoles: ['OPERATOR_EXTERNAL'] },
    ],
  },
  {
    name: 'T-Mobile',
    icon: BarChart3,
    requiredOperator: 'TMOBILE',
    isSection: true,
    operatorKey: 'TMOBILE',
    items: [
      { name: 'Analiza sprzedaży', href: '/tmobile-salon-analysis' },
      { name: 'Sprzedaż per Agent', href: '/tmobile-agent-sales' },
    ],
  },
  {
    name: 'Play',
    icon: BarChart3,
    requiredOperator: 'P4',
    isSection: true,
    operatorKey: 'P4',
    items: [
      { name: 'Analiza sprzedaży', href: '/play-salon-analysis' },
    ],
  },
  { type: 'separator' },
  {
    name: 'Administracja',
    icon: Settings,
    isSection: true,
    isAdminSection: true,
    requiredRoles: ['ADMIN', 'MANAGER'],
    items: [
      { name: 'Zarządzanie użytkownikami', href: '/admin/users', icon: Users, requiredRole: 'ADMIN' },
      { name: 'Zarządzanie celami KAM',    href: '/admin/goals', icon: Target },
      { name: 'Konfiguracja wizualizacji', href: '/admin/visualization', icon: Table },
      { name: 'Kolejność operatorów',      href: '/admin/operator-order', icon: ArrowUpDown, requiredRole: 'ADMIN' },
      { name: 'Dni wolne',                 href: '/admin/days-off', icon: Calendar },
      { name: 'Grupy produktów',           href: '/admin/product-groups', icon: Layers, requiredRole: 'ADMIN' },
      { name: 'Marże',                     href: '/admin/margins', icon: DollarSign, requiredRole: 'ADMIN' },
      { name: 'Sync KAM ↔ raporty',        href: '/admin/kam-sync', icon: RefreshCcw, requiredRole: 'ADMIN' },
      { name: 'POS — agent / RKS',         href: '/admin/pos', icon: Users, requiredRole: 'ADMIN' },
      { name: 'E-mail (raporty)',          href: '/admin/email', icon: Mail, requiredRole: 'ADMIN' },
      { name: 'Integracje MCP',            href: '/settings/mcp', icon: LinkIcon },
    ],
  },
]

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation()
  const { user, hasOperatorAccess, hasFeatureAccess, isAdmin } = useUser()
  const [openSections, setOpenSections] = useState({})

  useEffect(() => { ensureFonts() }, [])

  const canSeeItem = (item) => {
    if (item.excludeRoles && item.excludeRoles.includes(user?.role)) return false
    if (item.requiredRole === 'ADMIN' && !isAdmin) return false
    if (item.requiredRoles && !item.requiredRoles.includes(user?.role)) return false
    if (item.requiredFeature && !hasFeatureAccess(item.requiredFeature)) return false
    if (item.requiredOperator && !hasOperatorAccess(item.requiredOperator)) return false
    return true
  }

  const filteredNavigation = navigation.filter(item => {
    if (item.type === 'separator' || item.type === 'sectionLabel') return true
    if (item.isAdminSection) {
      if (!canSeeItem(item)) return false
      const visible = item.items?.filter(i => canSeeItem(i)) || []
      return visible.length > 0
    }
    return canSeeItem(item)
  }).map(item => {
    if (item.items) {
      return { ...item, items: item.items.filter(i => canSeeItem(i)) }
    }
    return item
  })

  const toggleSection = (name) =>
    setOpenSections(prev => ({ ...prev, [name]: !prev[name] }))

  const isSectionActive = (items) =>
    items?.some(i => location.pathname === i.href)

  // Auto-expand current section.
  useEffect(() => {
    filteredNavigation.forEach(item => {
      if (item.isSection && isSectionActive(item.items)) {
        setOpenSections(prev => ({ ...prev, [item.name]: true }))
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 20,
            background: 'rgba(0,0,0,0.45)',
          }}
          className="lg:hidden"
        />
      )}

      <aside style={{
        width: 240,
        background: theme.bgSub,
        borderRight: `1px solid ${theme.border}`,
        display: 'flex', flexDirection: 'column',
        height: '100vh', flexShrink: 0,
        fontFamily: T.fontUI,
        color: theme.text,
        position: 'relative',
        zIndex: 30,
        transform: isOpen ? 'translateX(0)' : undefined,
      }} className={`sidebar-shell ${isOpen ? 'open' : ''}`}>
        {/* Brand header */}
        <div style={{
          padding: '20px 18px',
          display: 'flex', alignItems: 'center', gap: 10,
          borderBottom: `1px solid ${theme.border}`,
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: 7,
            background: `linear-gradient(135deg, ${theme.accent}, ${theme.text})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: 14, letterSpacing: -0.5,
          }}>K</div>
          <div style={{ lineHeight: 1.15, flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: theme.text, letterSpacing: -0.3 }}>KAM-SFA</div>
            <div style={{ fontSize: 11, color: theme.textDim }}>Locon • Sales</div>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden"
            style={{
              padding: 6, borderRadius: 6,
              border: 'none', background: 'transparent',
              color: theme.textDim, cursor: 'pointer',
              display: 'inline-flex',
            }}
          ><X size={18} /></button>
        </div>

        {/* Nav */}
        <nav style={{
          padding: '14px 8px', flex: 1,
          overflowY: 'auto',
        }}>
          {filteredNavigation.map((item, idx) => {
            if (item.type === 'separator') {
              return <div key={`sep-${idx}`} style={{
                height: 1, background: theme.border, margin: '14px 8px',
              }} />
            }
            if (item.type === 'sectionLabel') {
              return (
                <div key={`lbl-${idx}`} style={{
                  fontSize: 10, fontWeight: 700, color: theme.textDim,
                  letterSpacing: 0.8, textTransform: 'uppercase',
                  padding: '14px 12px 8px',
                }}>{item.label}</div>
              )
            }

            // Section (operator or admin)
            if (item.isSection) {
              const isOpenSection = !!openSections[item.name]
              const hasActive = isSectionActive(item.items)
              const opColor = item.operatorKey ? OP_COLOR[item.operatorKey] : null
              return (
                <div key={item.name}>
                  <button
                    onClick={() => toggleSection(item.name)}
                    style={{
                      width: '100%',
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', marginBottom: 2,
                      borderRadius: 6, border: 'none', background: 'transparent',
                      color: hasActive ? theme.text : theme.textSub,
                      fontSize: 13, fontWeight: hasActive ? 600 : 500,
                      cursor: 'pointer', textAlign: 'left',
                      fontFamily: T.fontUI,
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = theme.surface}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    {opColor ? (
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: opColor, flexShrink: 0 }} />
                    ) : item.icon ? (
                      <item.icon size={15} style={{ color: theme.textDim, flexShrink: 0 }} />
                    ) : null}
                    <span style={{ flex: 1 }}>{item.name}</span>
                    <span style={{
                      transform: isOpenSection ? 'rotate(90deg)' : 'rotate(0)',
                      transition: 'transform 150ms', display: 'inline-flex',
                      color: theme.textDim, fontSize: 11,
                    }}>›</span>
                  </button>
                  {isOpenSection && (
                    <div style={{ paddingLeft: 18, marginBottom: 6 }}>
                      {item.items.map(sub => {
                        const isActive = location.pathname === sub.href
                        const SubIcon = sub.icon
                        return (
                          <Link key={sub.href} to={sub.href} onClick={onClose}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '6px 12px', marginBottom: 1,
                              borderRadius: 5,
                              fontSize: 12.5, fontWeight: isActive ? 600 : 400,
                              color: isActive ? theme.text : theme.textSub,
                              background: isActive ? theme.surface : 'transparent',
                              border: isActive ? `1px solid ${theme.border}` : '1px solid transparent',
                              boxShadow: isActive ? `0 1px 2px ${theme.border}` : 'none',
                              textDecoration: 'none',
                              position: 'relative',
                            }}
                            onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = theme.surface }}
                            onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent' }}
                          >
                            {isActive && opColor && (
                              <span style={{
                                position: 'absolute', left: -1, top: 6, bottom: 6, width: 2,
                                background: opColor, borderRadius: 2,
                              }} />
                            )}
                            {isActive && !opColor && (
                              <span style={{
                                position: 'absolute', left: -1, top: 6, bottom: 6, width: 2,
                                background: theme.accent, borderRadius: 2,
                              }} />
                            )}
                            {SubIcon && <SubIcon size={13} style={{ color: theme.textDim, flexShrink: 0 }} />}
                            <span style={{ flex: 1 }}>{sub.name}</span>
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            }

            // Top-level link
            const ItemIcon = item.icon
            const isActive = location.pathname === item.href ||
                            (item.href === '/dashboard' && location.pathname.startsWith('/dashboard'))
            return (
              <Link key={item.href} to={item.href} onClick={onClose}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 12px', marginBottom: 2,
                  borderRadius: 6,
                  fontSize: 13, fontWeight: isActive ? 600 : 500,
                  color: isActive ? theme.text : theme.textSub,
                  background: isActive ? theme.surface : 'transparent',
                  border: isActive ? `1px solid ${theme.border}` : '1px solid transparent',
                  boxShadow: isActive ? `0 1px 2px ${theme.border}` : 'none',
                  textDecoration: 'none', position: 'relative',
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = theme.surface }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent' }}
              >
                {isActive && (
                  <span style={{
                    position: 'absolute', left: -1, top: 8, bottom: 8, width: 2,
                    background: theme.accent, borderRadius: 2,
                  }} />
                )}
                {ItemIcon && (
                  <ItemIcon size={15} style={{
                    color: isActive ? theme.accent : theme.textDim,
                    flexShrink: 0,
                  }} />
                )}
                <span>{item.name}</span>
              </Link>
            )
          })}
        </nav>

        {/* Footer info */}
        <div style={{
          padding: '12px 18px',
          borderTop: `1px solid ${theme.border}`,
          fontSize: 11, color: theme.textDim, lineHeight: 1.4,
        }}>
          Wydajność KAM i operatorów telekomunikacyjnych.
        </div>
      </aside>

      {/* Mobile slide-in CSS */}
      <style>{`
        @media (max-width: 1024px) {
          .sidebar-shell {
            position: fixed !important;
            inset-y: 0; left: 0;
            transform: translateX(-100%);
            transition: transform 200ms ease;
          }
          .sidebar-shell.open { transform: translateX(0); }
        }
      `}</style>
    </>
  )
}

export default Sidebar
